import React from 'react';
import { createCodebookCache } from '../utils/codebookCache';

export interface MachineOption {
  _id: string;
  code: string;
  name: string;
  setupTime?: number | null;             // Времетраење на монтажа
  cleaningDismountTime?: number | null;  // Времетраење на демонтажа и чистење
  setupTimeUnit?: { _id?: string; code?: string; unitMeasurment?: string; name?: string } | string | null; // ЕМ
  ru?: ({ _id: string; code?: string; nameMK?: string; nameEN?: string } | string)[] | { _id: string; code?: string; nameMK?: string; nameEN?: string } | string | null;          // РУ (монтажа)
  ruHandling?: ({ _id: string; code?: string; nameMK?: string; nameEN?: string } | string)[] | { _id: string; code?: string; nameMK?: string; nameEN?: string } | string | null;  // РУ (ракување)
}

// Size variants, same convention as SpreadsheetItemInput / SpreadsheetPartnerInput.
// `table` exists so spreadsheet cells stop bending this component from the call site with
// `!important` utility overrides (CLAUDE.md rule 7b) — OSProductivityTable used to pass
// `!h-[24px] !text-[11px] !border-0 !rounded-none !px-1 !ring-0`. Focus state is the
// canonical blue accent, matching the sibling cells in those tables.
// Font size is NOT set here — the global form-control rule in index.css gives every
// field 13px. Adding text-* back would be dead code.
const SIZE_STYLES = {
  default: 'h-10 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200',
  table: 'h-6 px-1 border-0 rounded-none outline-none hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50',
} as const;

export type MachineSelectSize = keyof typeof SIZE_STYLES;

interface MachineSelectProps {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  allowedMachineIds?: string[];
  placeholder?: string;
  className?: string;
  size?: MachineSelectSize;
  onKeyDown?: (e: React.KeyboardEvent<HTMLSelectElement>) => void;
  displayNameOnly?: boolean;
  hideArrow?: boolean;
}

// ─── Machine cache (via shared codebook-cache factory) ───────────────────────
// SWR + subscriber broadcast + auto-invalidate on writes to /api/machines all
// come from the factory — see utils/codebookCache.ts.

// /active is open to every module with a machine picker (subprocesses, processes, normativ, RN),
// not only to the Machines module, and returns the whole list (the plain list used to stop at 500).
const machineCache = createCodebookCache<MachineOption>({
  endpoint: '/api/machines/active',
  invalidateOn: '/api/machines',
  select: (data) => (Array.isArray(data) ? data : []),
});

/** Call after any successful create/update/delete so next mount fetches fresh data. */
export const invalidateMachineCache = machineCache.invalidate;
/** Force a fetch now and broadcast to all mounted selects. */
export const refetchMachineOptions = machineCache.refetch;
/** Hook: active machine options (stale-while-revalidate, live-updating). */
export const useMachineOptions = (): MachineOption[] => machineCache.useOptions();

const MachineSelect = React.forwardRef<HTMLSelectElement, MachineSelectProps>(({
  value,
  onChange,
  disabled = false,
  allowedMachineIds,
  placeholder = '—',
  className = '',
  size = 'default',
  onKeyDown,
  displayNameOnly = false,
  hideArrow = false,
}, ref) => {
  const options = useMachineOptions();
  const allowedMachineSet = React.useMemo(
    () => allowedMachineIds && allowedMachineIds.length > 0 ? new Set(allowedMachineIds) : null,
    [allowedMachineIds]
  );
  const visibleOptions = React.useMemo(
    () => allowedMachineSet ? options.filter((m) => allowedMachineSet.has(m._id) || m._id === value) : options,
    [allowedMachineSet, options, value]
  );
  // A borderless cell sits on the row's own background; only the standalone control paints white.
  const background = disabled
    ? 'bg-gray-50 cursor-not-allowed'
    : size === 'table'
    ? 'bg-transparent'
    : 'bg-white';

  return (
    <select
      ref={ref}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      onMouseDown={() => refetchMachineOptions()}
      onFocus={() => refetchMachineOptions()}
      onKeyDown={onKeyDown}
      disabled={disabled}
      className={`${SIZE_STYLES[size]} ${background} ${hideArrow ? 'appearance-none' : ''} ${className}`}
    >
      <option value="">{placeholder}</option>
      {visibleOptions.map((m) => (
        <option key={m._id} value={m._id}>
          {displayNameOnly ? m.name : `${m.code} — ${m.name}`}
        </option>
      ))}
    </select>
  );
});

export default MachineSelect;
